import { convertEmptyStringsToUndefined, trimStrings } from '@ui-common/utils'
import { FormDevTool } from '@ui-common/utils/FormDevTool'
import { ReactNode, useCallback } from 'react'
import {
  DefaultValues,
  FieldErrors,
  FieldValues,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
  UseFormProps,
  UseFormReturn,
} from 'react-hook-form'
import * as R from 'remeda'

import { useScrollToFormError } from './useScrollToFormError'

export interface UseWizardFormReturn<TFieldValues extends FieldValues> extends UseFormReturn<TFieldValues> {
  FormDevTool: () => ReactNode
}

export interface UseWizardFormProps<TFieldValues extends FieldValues = FieldValues> extends UseFormProps<TFieldValues> {
  shouldConvertEmptyStringsToUndefined?: boolean
  hasAsyncValidation?: boolean
}

export const useWizardFormShouldUnregisterAsFalse = <TFieldValues extends FieldValues = FieldValues>({
  resolver,
  defaultValues = {} as DefaultValues<TFieldValues>,
}: UseWizardFormProps<TFieldValues> = {}): UseWizardFormReturn<TFieldValues> => {
  return useWizardForm({ resolver, defaultValues, shouldUnregister: false })
}

export const useWizardForm = <TFieldValues extends FieldValues = FieldValues>({
  resolver,
  defaultValues = {} as DefaultValues<TFieldValues>,
  shouldUnregister = true,
  mode = 'onTouched',
  shouldConvertEmptyStringsToUndefined = true,
}: UseWizardFormProps<TFieldValues> = {}): UseWizardFormReturn<TFieldValues> => {
  const { handleSubmit, formState, ...rest } = useForm<TFieldValues>({
    mode,
    reValidateMode: 'onChange',
    criteriaMode: 'firstError',
    shouldFocusError: false,
    resolver,
    defaultValues,
    shouldUnregister,
  })

  const { scrollToFirstErroredFormElement } = useScrollToFormError()

  const wrappedHandleSubmit = useCallback(
    (submitHandler: SubmitHandler<TFieldValues>, errorHandler?: SubmitErrorHandler<TFieldValues>) => {
      const processFormData = (data: TFieldValues) => {
        // Data has to be cloned because react-hook-form can mutate the data after submit
        const commonParams = [data, R.clone, trimStrings] as const
        return shouldConvertEmptyStringsToUndefined
          ? R.pipe(...commonParams, convertEmptyStringsToUndefined)
          : R.pipe(...commonParams)
      }

      const wrappedSubmitHandler = async (data: TFieldValues) => {
        const processedFormData = processFormData(data)
        await submitHandler(processedFormData as TFieldValues)
      }
      const wrappedErrorHandler = (errors: FieldErrors<TFieldValues>) => {
        scrollToFirstErroredFormElement(errors)
        if (errorHandler) {
          errorHandler(errors)
        }
      }
      return handleSubmit(wrappedSubmitHandler, wrappedErrorHandler)
    },
    [handleSubmit, scrollToFirstErroredFormElement, shouldConvertEmptyStringsToUndefined],
  )

  return {
    handleSubmit: wrappedHandleSubmit,
    FormDevTool,
    formState,
    ...rest,
  }
}
